import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { ArrowRight, User, X } from 'lucide-react';
import { feedItemBodyHtml } from 'utils/KnockFeedUtils';
import sanitizeFeedHtml from 'utils/sanitizeFeedHtml';
import { bannerAvatarView } from './bannerAvatarView';
import { bannerIdentity } from './bannerIdentity';
/**
 * Coach avatar with a load-failure fallback (SCO-491). `bannerIdentity` only
 * nulls the url when trigger data fails to PARSE — a well-formed url that 404s
 * still arrives as a string, so without `onError` the banner rendered a
 * broken-image glyph and leaked its alt text into the coaching message.
 */
var BannerAvatar = function (_a) {
    var url = _a.url;
    // Which url failed, not a boolean: the banner swaps items in place, so a
    // later message with a different avatar must get a fresh attempt rather
    // than inheriting the previous one's failure.
    var _b = useState(null), failedUrl = _b[0], setFailedUrl = _b[1];
    var view = bannerAvatarView(url, failedUrl);
    // Coral ring is the brand tell that a coach — not the app — is speaking.
    var ring = 'h-10 w-10 shrink-0 rounded-full ring-2 ring-red/40';
    if (view.kind === 'image') {
        return (_jsx("img", { src: view.url, 
            // Decorative: empty alt, not a name. A broken image paints its alt
            // text inline, which is how "You" ended up mid-sentence in the
            // coaching message — `aria-hidden` hides it from AT but not from
            // sighted users during the gap before `onError` commits.
            alt: "", "aria-hidden": "true", onError: function () { return setFailedUrl(view.url); }, className: "".concat(ring, " object-cover") }));
    }
    return (_jsx("span", { "aria-hidden": "true", className: "".concat(ring, " flex items-center justify-center bg-light-slate text-slate"), children: _jsx(User, { className: "h-5 w-5" }) }));
};
/*
 * Shared shell (SCO-492). A floating light card, not the old full-bleed
 * dark-slate bar — that read as an outage notice rather than encouragement
 * from a coach. Inset and rounded so page content stays visible either side,
 * with the coral left edge as the brand tell.
 *
 * Fixed positioning anchors it to the viewport, so one component serves the
 * player area (desktop web) and the mobile entry (which the iOS shell wraps in
 * a webview) without caring where it mounts. `top-[62px]` clears BOTH 54px
 * fixed headers with an 8px gap — at top-0 it covered the header and
 * click-blocked the bell, nav, and account menu until acknowledged.
 */
var SHELL = 'fixed inset-x-2 top-[62px] z-[100] mx-auto flex max-w-2xl items-start gap-3 rounded-lg border border-border border-l-4 border-l-red bg-card p-4 text-left shadow-lg sm:inset-x-4';
/** Bebas Neue eyebrow — who is speaking, in the display face. */
var EYEBROW = 'block font-display text-sm leading-none tracking-wider text-slate';
var BODY = 'mt-1 block text-sm leading-snug text-foreground [&_p]:m-0';
var ProducerNudgeBanner = function (_a) {
    var item = _a.item, onAcknowledge = _a.onAcknowledge, onRespond = _a.onRespond, onOpenAction = _a.onOpenAction, interactive = _a.interactive, dismissable = _a.dismissable;
    var _b = bannerIdentity(item), label = _b.label, avatarUrl = _b.avatarUrl;
    var message = (_jsxs("span", { className: "min-w-0 flex-1", children: [_jsx("span", { className: EYEBROW, children: label }), _jsx("span", { className: BODY, dangerouslySetInnerHTML: {
                    __html: sanitizeFeedHtml(feedItemBodyHtml(item.blocks))
                } })] }));
    /*
     * Expired. Dismissal becomes necessary here — an ignored check-in never gets
     * `interacted_at`, and `selectBannerItem` keys off exactly that, so without a
     * way out this banner is pinned to the top of the viewport until the 8h bell
     * rolloff. Server-side archival usually clears it at escalation, but that
     * path is best-effort ("never throws") on a 30-min tick, so the producer must
     * not have to depend on it.
     *
     * It only becomes SAFE once `dismissable` — far enough past the window that
     * clock skew cannot explain the expiry. Before that, inert text: a stuck
     * banner for a few minutes beats a dismiss button that silently kills a live
     * ask.
     */
    var expiredAction = dismissable ? (_jsx("button", { type: "button", onClick: onAcknowledge, className: "shrink-0 self-center rounded-base border border-border px-3 py-1.5 text-xs font-semibold text-slate hover:bg-light-slate", children: "Dismiss" })) : (_jsx("span", { className: "shrink-0 self-center text-xs text-slate", children: "Expired" }));
    /*
     * The attendance ask (SCO-470). NOT a button-wrapped banner: the whole
     * surface being tappable is what makes dismissal reachable, and a dismissed
     * check-in is unanswerable — the row then escalates to the coach digest as
     * "hasn't responded" for someone who tried to reply. Two explicit actions,
     * nothing else clickable.
     */
    if (onRespond) {
        return (_jsxs("div", { role: "group", "aria-label": "Morning check-in", className: SHELL, children: [avatarUrl === null ? null : _jsx(BannerAvatar, { url: avatarUrl }), message, interactive ? (_jsxs("span", { className: "flex shrink-0 gap-2 self-center", children: [_jsx("button", { type: "button", onClick: function () { return onRespond('yes'); }, className: "rounded-base bg-red px-4 py-1.5 text-sm font-semibold text-white shadow-button hover:opacity-90", children: "Yes" }), _jsx("button", { type: "button", onClick: function () { return onRespond('no'); }, className: "rounded-base border border-border bg-card px-4 py-1.5 text-sm font-semibold text-slate hover:bg-light-slate", children: "No" })] })) : (expiredAction)] }));
    }
    /*
     * The scoring nudge (SCO-504): whole-surface tap navigates to the scoring
     * screen — the tap means "let's go", not just "I heard you" — with
     * dismissal as its own control since the two intents differ here. A div
     * shell: nesting the dismiss button inside a tap-target button would be
     * invalid HTML.
     */
    if (onOpenAction) {
        return (_jsxs("div", { role: "group", "aria-label": "Coach nudge", className: SHELL, children: [avatarUrl === null ? null : _jsx(BannerAvatar, { url: avatarUrl }), _jsxs("button", { type: "button", onClick: onOpenAction, className: "flex min-w-0 flex-1 items-start gap-3 text-left", children: [message, _jsxs("span", { "aria-hidden": "true", className: "flex shrink-0 items-center gap-1 self-center text-xs font-semibold text-red", children: ["Open", _jsx(ArrowRight, { className: "h-3.5 w-3.5" })] })] }), _jsx("button", { type: "button", onClick: onAcknowledge, "aria-label": "Dismiss", className: "shrink-0 self-center p-1 text-slate hover:text-foreground", children: _jsx(X, { className: "h-3.5 w-3.5" }) })] }));
    }
    // No aria-label on the button: it would REPLACE the accessible name and
    // hide the coaching message from assistive tech — the name is the rendered
    // message body plus the "Dismiss" hint (avatars are decorative,
    // aria-hidden). Whole-surface tap is deliberate for a nudge: the tap means
    // "I heard you", and first-acknowledgment-wins clears it on every surface.
    return (_jsxs("button", { type: "button", onClick: onAcknowledge, className: SHELL, children: [avatarUrl === null ? null : _jsx(BannerAvatar, { url: avatarUrl }), message, _jsxs("span", { "aria-hidden": "true", className: "flex shrink-0 items-center gap-1 self-center text-xs text-slate", children: ["Dismiss", _jsx(X, { className: "h-3.5 w-3.5" })] })] }));
};
export default ProducerNudgeBanner;
